import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {AbstractComponent, AddUserModalComponent, AddUserResult, ConfirmationModalComponent} from "@components";
import {
  ActionResult,
  DEFAULT_PAGE_SIZE,
  FindUsersDetailsRequest,
  getRoleIdForRole,
  HELPER_ROLE,
  Organiser,
  ORGANIZER_ROLE, TOAST_STATE,
  USER_ROLE,
  UserDetail,
  UsersDetailFilter
} from "@model";
import {
  AuthenticationService,
  ExportService,
  OrganiserService,
  OrganiserUsersService,
  ToastService,
  UserDetailService
} from "@services";
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'app-organiser-user-list',
    templateUrl: './organiser-user-list.component.html',
    styleUrls: ['./organiser-user-list.component.scss'],
    standalone: false
})
export class OrganiserUserListComponent extends AbstractComponent {
  @Input() users: UserDetail[] | undefined;
  @Input() currentOrganiser: Organiser | undefined;
  @Output() onUsersChange: EventEmitter<UserDetail[] | undefined> = new EventEmitter<UserDetail[] | undefined>();

  currentUserDetails: UserDetail | undefined;
  environment: any = environment;
  searchText: string = "";
  page: number = 1;
  selectedRole: number | undefined;
  roles: number[] = UsersDetailFilter.ROLES;
  hasMoreUsers: boolean = true;
  isLoading: boolean = false;
  isAdmin: boolean = false;

  constructor(private userDetailsService: UserDetailService,
              private translocoService: TranslocoService,
              private toastService: ToastService,
              private modalService: NgbModal,
              private organiserUserService: OrganiserUsersService,
              private organiserService: OrganiserService,
              private exportService: ExportService,
              private route: ActivatedRoute,
              private authService: AuthenticationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.push(this.userDetailsService.currentUserDetails.subscribe((userDetails: UserDetail | undefined) => {
      this.currentUserDetails = userDetails;
    }));
    this.isAdmin = this.authService.isOrganisersAdministrator() || this.authService.isAdministrator();
  }



  onExplore(loadMore?: boolean) {
    //TODO - this endpoint does not support paging
    // if (loadMore !== true) {
    //   this.page = 1;
    //   this.hasMoreUsers = true;
    // }
    // if (this.hasMoreUsers) {
    //   let filter: FindUsersDetailsRequest = this.createFilter();
    //   this.organiserUserService.findUsers(filter, !loadMore, () => {
    //     this.isLoading = loadMore ? this.isLoading : true;
    //   }, () => {
    //     setTimeout(() => {
    //       this.isLoading = loadMore ? this.isLoading : false;
    //     }, 700);
    //   }).subscribe((value: UserDetail[]) => {
    //       this.handleExploreResult(value, loadMore);
    //     }
    //   );
    // }
  }

  handleExploreResult(users: UserDetail[], loadMore?: boolean) {
    if (users.length > DEFAULT_PAGE_SIZE) {
      this.hasMoreUsers = true;
      users = users.slice(0, DEFAULT_PAGE_SIZE);
    } else {
      this.hasMoreUsers = false;
    }
    if (loadMore && this.users) {
      users = this.users.concat(users);
    }
    this.setListOfItems(users);
  }

  setListOfItems(value: UserDetail[] | null) {
    if (value && value.length > 0) {
      this.users = value;
      this.page++;
    } else {
      this.users = [];
    }
  }


  openAddNewUserModal() {
    const modalRef: NgbModalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.add.new");
    modalRef.componentInstance.isAdmin = false;

    modalRef.result.then((result: AddUserResult) => {
      this.onAddUser(result.email!, result.roleId!);
    }, (_) => {
      /// ignore
    });
  }

  onAddUser(email: string, roleId: number): void {
    this.organiserUserService.addUser(email, roleId, this.currentOrganiser?.id).subscribe(result => this.handleResponse(result))
  }

  openEditUserModal(user: UserDetail) {
    const modalRef: NgbModalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.edit.user");
    modalRef.componentInstance.isAdmin = false;
    modalRef.componentInstance.isEditUser = true;
    modalRef.componentInstance.email = user.email ?? user.username;
    modalRef.componentInstance.roleId = getRoleIdForRole(user.role);

    modalRef.result.then((result: AddUserResult) => {
      if (user.userId) {
        this.onEditUser(user.userId!, result.roleId!);
      }
    }, (_) => {
      /// ignore
    });
  }

  onEditUser(userId: number, roleId: number): void {
    this.organiserUserService.editUser(userId, roleId, this.currentOrganiser?.id).subscribe(result => this.handleResponse(result))
  }

  openDeleteUserDialog(user: UserDetail) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("organiser.list.delete.dialog.text");

    modalRef.result.then((_) => {
      this.onDeleteUser(user);
    }, (_) => {
      /// ignore
    });
  }

  onDeleteUser(user: UserDetail): void {
    if (user.userId) {
      this.organiserUserService.deleteUser(user.userId!, this.currentOrganiser?.id).subscribe(result => this.handleResponse(result))
    }
  }

  handleResponse(result: ActionResult<boolean>) {
    if (result.success) {
      this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("organiser.users.add.success"));
      this.organiserUserService.findUsers(this.currentOrganiser?.id).subscribe((users: UserDetail[]) => {
        this.users = users;
        this.onUsersChange.emit(users);
      })
    } else {
      if (result.success === false &&
          (result.resultCode === OrganiserUsersService.USER_ALREADY_ASSIGNED
          || result.resultCode === OrganiserUsersService.USER_NOT_FOUND
              || result.resultCode === OrganiserUsersService.USER_ROLE_CHANGE_NOT_ALLOWED)) {
        this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.users.add.error." + result.resultCode));
      } else {
        this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("organiser.users.add.error"));
      }
    }
  }


  private createFilter() {
    return FindUsersDetailsRequest.createUsersDetailsRequest(
      this.page,
      {
        text: this.searchText !== "" ? this.searchText : undefined,
        role: this.selectedRole ?? undefined
      });
  }

  protected readonly ORGANIZER_ROLE: string = ORGANIZER_ROLE;
  protected readonly HELPER_ROLE: string = HELPER_ROLE;
  protected readonly USER_ROLE: string = USER_ROLE;
}


